import { useEffect, useMemo, useState } from 'react';
import {
    Box, Typography, Button, Stack, Switch, IconButton, Dialog,
    DialogTitle, DialogContent, DialogActions, CircularProgress, Alert,
    TextField, MenuItem, Divider, Chip, FormControlLabel,
} from '@mui/material';
import { DataGrid, type GridColDef } from '@mui/x-data-grid';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '../api/client';


type AdminPrice = {
    priceId: string;
    pricePerPlace: number | null;
    currency: string;
    discountPercent: number | null;
    priceDescriptionShort: string;
    priceDescriptionLong: string;
    priceIncludesVat: boolean;
    applicationOrder: number | null;
    isValidForDays: number | null;
    isValidForWeeks: number | null;
    isValidForMonths: number | null;
    filterUserOnly: boolean;
    filterCompanyOnly: boolean;
    filterRegion: string | null;
    filterCountryCode: string | null;
    filterCompanyGroup: string | null;
    filterActionCode: string | null;
    filterDateGroup: string | null;
    filterPlaceCountFrom: number | null;
    filterPlaceCountTo: number | null;
    filterActiveFrom: string | null;
    filterActiveTo: string | null;
    active: boolean;
};


const EMPTY_PRICE: AdminPrice = {
    priceId: '',
    pricePerPlace: 6.00,
    currency: 'eur',
    discountPercent: null,
    priceDescriptionShort: '',
    priceDescriptionLong: '',
    priceIncludesVat: true,
    applicationOrder: 100,
    isValidForDays: null,
    isValidForWeeks: null,
    isValidForMonths: 12,
    filterUserOnly: false,
    filterCompanyOnly: false,
    filterRegion: null,
    filterCountryCode: null,
    filterCompanyGroup: null,
    filterActionCode: null,
    filterDateGroup: null,
    filterPlaceCountFrom: null,
    filterPlaceCountTo: null,
    filterActiveFrom: null,
    filterActiveTo: null,
    active: true,
};


export function PricesPage() {
    const queryClient = useQueryClient();
    const [editing, setEditing] = useState<AdminPrice | null>(null);
    const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);

    const { data: prices, isLoading, error } = useQuery({
        queryKey: ['admin-prices'],
        queryFn: async () => {
            const resp = await api.get<{ items: AdminPrice[] }>('/api/v1/admin/prices');
            return resp.data.items;
        },
    });

    const saveMutation = useMutation({
        mutationFn: async (p: AdminPrice) => {
            const body = { ...p, priceId: undefined };
            if (p.priceId) {
                await api.put(`/api/v1/admin/prices/${p.priceId}`, body);
            } else {
                await api.post('/api/v1/admin/prices', body);
            }
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['admin-prices'] });
            setEditing(null);
        },
    });

    const deleteMutation = useMutation({
        mutationFn: async (priceId: string) => {
            await api.delete(`/api/v1/admin/prices/${priceId}`);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['admin-prices'] });
            setConfirmDeleteId(null);
        },
    });

    const columns = useMemo<GridColDef<AdminPrice>[]>(() => [
        {
            field: 'applicationOrder', headerName: 'Order', width: 80,
            valueFormatter: (v?: number) => v ?? '—',
        },
        { field: 'priceDescriptionShort', headerName: 'Nosaukums', flex: 1, minWidth: 200 },
        {
            field: 'pricePerPlace', headerName: 'Cena', width: 110,
            valueFormatter: (v?: number) => v != null ? `${v.toFixed(2)} EUR` : '—',
        },
        {
            field: 'validity', headerName: 'Derīgums', width: 110,
            valueGetter: (_v, row) =>
                row.isValidForMonths ? `${row.isValidForMonths} mēn.`
                : row.isValidForWeeks ? `${row.isValidForWeeks} ned.`
                : row.isValidForDays  ? `${row.isValidForDays} d.`
                : '—',
        },
        {
            field: 'filters', headerName: 'Filtri', flex: 1, minWidth: 200,
            renderCell: (p) => {
                const tags: string[] = [];
                if (p.row.filterUserOnly) tags.push('User-only');
                if (p.row.filterCompanyOnly) tags.push('Company-only');
                if (p.row.filterCountryCode) tags.push(`Country=${p.row.filterCountryCode}`);
                if (p.row.filterPlaceCountFrom != null || p.row.filterPlaceCountTo != null) {
                    tags.push(`Places ${p.row.filterPlaceCountFrom ?? '*'}–${p.row.filterPlaceCountTo ?? '*'}`);
                }
                if (p.row.filterActiveFrom || p.row.filterActiveTo) tags.push('Date range');
                return tags.length === 0
                    ? <span style={{ color: '#888' }}>—</span>
                    : <Stack direction="row" gap={0.5} flexWrap="wrap">
                        {tags.map(t => <Chip key={t} label={t} size="small" />)}
                      </Stack>;
            },
        },
        {
            field: 'active', headerName: 'Statuss', width: 100,
            renderCell: (p) => p.value
                ? <Chip label="Aktīva" color="success" size="small" />
                : <Chip label="Slēgta" size="small" />,
        },
        {
            field: 'actions', headerName: '', width: 100, sortable: false, filterable: false,
            renderCell: (p) => (
                <Stack direction="row">
                    <IconButton size="small" onClick={() => setEditing(p.row)}>
                        <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                        size="small"
                        color="error"
                        onClick={() => setConfirmDeleteId(p.row.priceId)}
                    >
                        <DeleteIcon fontSize="small" />
                    </IconButton>
                </Stack>
            ),
        },
    ], []);

    return (
        <Box>
            <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
                <Typography variant="h4">Cenas</Typography>
                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => setEditing({ ...EMPTY_PRICE })}
                >
                    Pievienot
                </Button>
            </Stack>

            {error && <Alert severity="error" sx={{ mb: 2 }}>Neizdevās ielādēt cenu sarakstu.</Alert>}

            <Box sx={{ height: 'calc(100vh - 220px)', minHeight: 400, bgcolor: 'background.paper' }}>
                <DataGrid
                    rows={prices || []}
                    columns={columns}
                    getRowId={(r) => r.priceId}
                    getRowHeight={() => 'auto'}
                    loading={isLoading}
                    disableRowSelectionOnClick
                    initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
                    pageSizeOptions={[25, 50, 100]}
                />
            </Box>

            <PriceDialog
                price={editing}
                onClose={() => setEditing(null)}
                onSave={(p) => saveMutation.mutate(p)}
                isSaving={saveMutation.isPending}
            />

            <Dialog open={Boolean(confirmDeleteId)} onClose={() => setConfirmDeleteId(null)}>
                <DialogTitle>Dzēst cenu?</DialogTitle>
                <DialogContent>
                    Cena tiks soft-dzēsta (deleted=true) un vairs neparādīsies sarakstā vai
                    PriceResolverService aprēķinos.
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmDeleteId(null)}>Atcelt</Button>
                    <Button
                        color="error"
                        variant="contained"
                        onClick={() => confirmDeleteId && deleteMutation.mutate(confirmDeleteId)}
                        disabled={deleteMutation.isPending}
                    >
                        Dzēst
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}


function PriceDialog({
    price, onClose, onSave, isSaving,
}: {
    price: AdminPrice | null;
    onClose: () => void;
    onSave: (p: AdminPrice) => void;
    isSaving: boolean;
}) {
    const [form, setForm] = useState<AdminPrice>(EMPTY_PRICE);
    const [validityUnit, setValidityUnit] = useState<'days' | 'weeks' | 'months'>('months');
    const [validityValue, setValidityValue] = useState<string>('12');

    useEffect(() => {
        if (price) {
            setForm(price);
            if (price.isValidForDays != null) {
                setValidityUnit('days'); setValidityValue(String(price.isValidForDays));
            } else if (price.isValidForWeeks != null) {
                setValidityUnit('weeks'); setValidityValue(String(price.isValidForWeeks));
            } else {
                setValidityUnit('months'); setValidityValue(String(price.isValidForMonths ?? 12));
            }
        }
    }, [price]);

    const update = <K extends keyof AdminPrice>(key: K, value: AdminPrice[K]) =>
        setForm(prev => ({ ...prev, [key]: value }));

    const submit = () => {
        const v = parseInt(validityValue || '0', 10);
        const validityFields: Pick<AdminPrice, 'isValidForDays' | 'isValidForWeeks' | 'isValidForMonths'> = {
            isValidForDays:   validityUnit === 'days'   ? v : null,
            isValidForWeeks:  validityUnit === 'weeks'  ? v : null,
            isValidForMonths: validityUnit === 'months' ? v : null,
        };
        onSave({ ...form, ...validityFields });
    };

    return (
        <Dialog open={Boolean(price)} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>{price?.priceId ? 'Rediģēt cenu' : 'Pievienot cenu'}</DialogTitle>
            <DialogContent>
                <Stack spacing={3} sx={{ mt: 1 }}>
                    {/* Basic info */}
                    <Box>
                        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                            Pamata informācija
                        </Typography>
                        <Stack spacing={2}>
                            <TextField
                                label="Īsais nosaukums"
                                value={form.priceDescriptionShort}
                                onChange={(e) => update('priceDescriptionShort', e.target.value)}
                                required
                                fullWidth
                            />
                            <TextField
                                label="Garais apraksts"
                                value={form.priceDescriptionLong}
                                onChange={(e) => update('priceDescriptionLong', e.target.value)}
                                multiline
                                rows={2}
                                fullWidth
                            />
                            <Stack direction="row" spacing={2}>
                                <TextField
                                    label="Cena par vietu"
                                    type="number"
                                    value={form.pricePerPlace ?? ''}
                                    onChange={(e) => update('pricePerPlace',
                                        e.target.value === '' ? null : parseFloat(e.target.value))}
                                    inputProps={{ step: '0.01', min: '0' }}
                                    sx={{ flex: 1 }}
                                />
                                <TextField
                                    label="Valūta"
                                    value={form.currency}
                                    onChange={(e) => update('currency', e.target.value.toLowerCase())}
                                    inputProps={{ maxLength: 3 }}
                                    sx={{ width: 100 }}
                                />
                                <TextField
                                    label="Atlaide %"
                                    type="number"
                                    value={form.discountPercent ?? ''}
                                    onChange={(e) => update('discountPercent',
                                        e.target.value === '' ? null : parseFloat(e.target.value))}
                                    inputProps={{ step: '0.01', min: '0', max: '100' }}
                                    sx={{ width: 120 }}
                                />
                            </Stack>
                            <Stack direction="row" spacing={2} alignItems="center">
                                <TextField
                                    label="Application order"
                                    helperText="Augstāks = uzvar pār zemāku, ja filtri atbilst"
                                    type="number"
                                    value={form.applicationOrder ?? ''}
                                    onChange={(e) => update('applicationOrder',
                                        e.target.value === '' ? null : parseInt(e.target.value, 10))}
                                    sx={{ width: 200 }}
                                />
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={form.priceIncludesVat}
                                            onChange={(e) => update('priceIncludesVat', e.target.checked)}
                                        />
                                    }
                                    label="Cena ar PVN"
                                />
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={form.active}
                                            onChange={(e) => update('active', e.target.checked)}
                                        />
                                    }
                                    label="Aktīva"
                                />
                            </Stack>
                        </Stack>
                    </Box>

                    <Divider />

                    {/* Validity */}
                    <Box>
                        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                            Derīguma periods
                        </Typography>
                        <Stack direction="row" spacing={2}>
                            <TextField
                                label="Skaits"
                                type="number"
                                value={validityValue}
                                onChange={(e) => setValidityValue(e.target.value)}
                                inputProps={{ min: '1' }}
                                sx={{ flex: 1 }}
                            />
                            <TextField
                                select
                                label="Vienība"
                                value={validityUnit}
                                onChange={(e) => setValidityUnit(e.target.value as 'days' | 'weeks' | 'months')}
                                sx={{ width: 200 }}
                            >
                                <MenuItem value="days">dienas</MenuItem>
                                <MenuItem value="weeks">nedēļas</MenuItem>
                                <MenuItem value="months">mēneši</MenuItem>
                            </TextField>
                        </Stack>
                    </Box>

                    <Divider />

                    {/* Filters */}
                    <Box>
                        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                            Filtri (kad cena tiek piemērota)
                        </Typography>
                        <Stack spacing={2}>
                            <Stack direction="row" spacing={2}>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={form.filterUserOnly}
                                            onChange={(e) => update('filterUserOnly', e.target.checked)}
                                        />
                                    }
                                    label="Tikai privātpersonām"
                                />
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={form.filterCompanyOnly}
                                            onChange={(e) => update('filterCompanyOnly', e.target.checked)}
                                        />
                                    }
                                    label="Tikai kompānijām"
                                />
                            </Stack>
                            <Stack direction="row" spacing={2}>
                                <TextField
                                    label="Valsts kods"
                                    value={form.filterCountryCode ?? ''}
                                    onChange={(e) => update('filterCountryCode', e.target.value || null)}
                                    helperText="ISO 2 burtu, piem., LV"
                                    inputProps={{ maxLength: 3 }}
                                    sx={{ width: 160 }}
                                />
                                <TextField
                                    label="Reģions"
                                    value={form.filterRegion ?? ''}
                                    onChange={(e) => update('filterRegion', e.target.value || null)}
                                    helperText="Nav implementēts"
                                    disabled
                                    sx={{ width: 160 }}
                                />
                                <TextField
                                    label="Kompāniju grupa"
                                    value={form.filterCompanyGroup ?? ''}
                                    onChange={(e) => update('filterCompanyGroup', e.target.value || null)}
                                    helperText="Nav implementēts"
                                    disabled
                                    sx={{ width: 160 }}
                                />
                                <TextField
                                    label="Datumu grupa"
                                    value={form.filterDateGroup ?? ''}
                                    onChange={(e) => update('filterDateGroup', e.target.value || null)}
                                    helperText="Nav implementēts"
                                    disabled
                                    sx={{ width: 160 }}
                                />
                            </Stack>
                            <Stack direction="row" spacing={2}>
                                <TextField
                                    label="Vietu skaits no"
                                    type="number"
                                    value={form.filterPlaceCountFrom ?? ''}
                                    onChange={(e) => update('filterPlaceCountFrom',
                                        e.target.value === '' ? null : parseInt(e.target.value, 10))}
                                    inputProps={{ min: '0' }}
                                    sx={{ flex: 1 }}
                                />
                                <TextField
                                    label="Vietu skaits līdz"
                                    type="number"
                                    value={form.filterPlaceCountTo ?? ''}
                                    onChange={(e) => update('filterPlaceCountTo',
                                        e.target.value === '' ? null : parseInt(e.target.value, 10))}
                                    inputProps={{ min: '0' }}
                                    sx={{ flex: 1 }}
                                />
                            </Stack>
                            <Stack direction="row" spacing={2}>
                                <TextField
                                    label="Aktīva no"
                                    type="datetime-local"
                                    value={form.filterActiveFrom?.slice(0, 16) ?? ''}
                                    onChange={(e) => update('filterActiveFrom', e.target.value || null)}
                                    InputLabelProps={{ shrink: true }}
                                    sx={{ flex: 1 }}
                                />
                                <TextField
                                    label="Aktīva līdz"
                                    type="datetime-local"
                                    value={form.filterActiveTo?.slice(0, 16) ?? ''}
                                    onChange={(e) => update('filterActiveTo', e.target.value || null)}
                                    InputLabelProps={{ shrink: true }}
                                    sx={{ flex: 1 }}
                                />
                            </Stack>
                        </Stack>
                    </Box>
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Atcelt</Button>
                <Button
                    variant="contained"
                    onClick={submit}
                    disabled={isSaving || !form.priceDescriptionShort.trim()}
                >
                    {isSaving ? <CircularProgress size={20} /> : 'Saglabāt'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
